import { Link } from "react-router-dom";
import { ArrowRight, Clock, FileCheck2, Phone } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { Hero } from "@/components/site/Hero";
import { PageShell } from "@/components/site/PageShell";
import { CtaBand, ExpertBox, FaqAccordion, Reviews, Rights, SectionHeading, ServiceArea, ServiceCards, Steps } from "@/components/sections/Sections";
import { GENERAL_FAQ, STEPS } from "@/data/content";
import { businessLd, faqLd } from "@/lib/jsonld";
import { SITE } from "@/lib/site";

const HOME_FAQ = GENERAL_FAQ.slice(0, 6);

const FACTS = [
  { icon: Phone, value: "24 h", label: "erreichbar Mo–Fr" },
  { icon: FileCheck2, value: "24–48 h", label: "bis zum Gutachten" },
  { icon: Clock, value: "30 Min.", label: "Antwortzeit online" },
];

const HeroActions = () => (
  <div className="mt-8 hidden flex-wrap gap-3 sm:flex">
    <a href={SITE.phoneHref} className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 font-bold text-brand-blue shadow-soft transition-transform hover:-translate-y-0.5" data-testid="hero-call-button">
      <Phone className="h-4 w-4" /> Jetzt anrufen
    </a>
    <Link to="/schaden-melden" className="inline-flex items-center gap-2 rounded-full bg-brand-yellow px-6 py-3.5 font-bold text-brand-anthracite shadow-soft transition-transform hover:-translate-y-0.5" data-testid="hero-damage-button">
      Schaden online melden <ArrowRight className="h-4 w-4" />
    </Link>
  </div>
);

export default function Home() {
  return (
    <>
      <Seo
        title="Kfz-Gutachter Köln – TÜV-zertifiziert, 24 h | Marcone"
        description="Kfz-Gutachter in Köln: TÜV-zertifiziert, unabhängig und 24 h erreichbar. Kostenlos bei unverschuldetem Unfall. Jetzt anrufen, WhatsApp oder Schaden melden."
        path="/"
        jsonLd={[businessLd(), faqLd(HOME_FAQ)]}
      />
      <PageShell
        overlap="home"
        hero={
          <Hero
            variant="home"
            image="real-marcone-auto"
            imagePosition="center 20%"
            imageAlt="Kfz-Sachverständiger Francesco Marcone dokumentiert einen Unfallschaden an einem Auto in Köln"
            eyebrow="Sofortdienst bei Auto-Unfällen · Großraum Köln"
            title="Kfz-Gutachter Köln – TÜV-zertifiziert, 24 h vor Ort"
            subtitle="TÜV-zertifiziert · werkstatt- & versicherungsunabhängig · kostenlos bei unverschuldetem Unfall"
          >
            <HeroActions />
          </Hero>
        }
      >
        <div className="space-y-20">
          <section aria-labelledby="intro">
            <p className="eyebrow mb-3">Analysen und Kalkulationen für Ihr Recht</p>
            <h2 id="intro" className="h2-brand text-2xl sm:text-3xl">Ihr unabhängiger Kfz-Sachverständiger in Köln</h2>
            <p className="mt-4 text-base leading-relaxed">
              Mit unserem Kfz-Gutachten erhalten Sie Analysen und Kalkulationen an die Hand, damit die Versicherung Ihre berechtigten Ansprüche
              anerkennt – für Instandsetzung, Schadenersatz, Nutzungsausfall und weitere Positionen. Rufen Sie uns gern an: Wir sind erfahren im
              Schadenmanagement und sorgen dafür, dass Ihre Rechte gewahrt bleiben.
            </p>
            <dl className="mt-8 grid gap-3 sm:grid-cols-3">
              {FACTS.map(({ icon: Icon, value, label }) => (
                <div key={label} className="rounded-2xl border border-slate-200 p-5">
                  <Icon className="h-5 w-5 text-brand-blue" />
                  <dt className="sr-only">{label}</dt>
                  <dd className="mt-3 text-2xl font-bold text-brand-anthracite">{value}</dd>
                  <dd className="text-sm text-brand-ink">{label}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section aria-label="Leistungen">
            <SectionHeading eyebrow="Leistungen" title="Gutachten für jeden Anlass" intro="Vom Unfallgutachten bis zum Oldtimer – neutral, nachvollziehbar und schnell." />
            <ServiceCards />
          </section>

          <section aria-label="Ablauf">
            <SectionHeading eyebrow="Ablauf" title="In 4 Schritten zu Ihrem Geld" intro="So einfach läuft die Schadenregulierung mit uns." />
            <Steps items={STEPS} prefix="home-steps" />
          </section>

          <section aria-label="Ihre Rechte"><Rights /></section>

          <section aria-label="Über uns"><ExpertBox /></section>

          <section aria-label="Bewertungen">
            <SectionHeading eyebrow="Kundenstimmen" title="5 Sterne von unseren Kunden" intro="Das sagen Kundinnen und Kunden über ihre Erfahrung mit unserem Gutachterbüro." />
            <Reviews />
          </section>

          <section aria-label="Häufige Fragen">
            <SectionHeading eyebrow="FAQ" title="Häufige Fragen zum Kfz-Gutachten" />
            <FaqAccordion items={HOME_FAQ} prefix="home" />
            <Link to="/faq" className="mt-6 inline-flex items-center gap-1.5 font-bold text-brand-blue hover:underline" data-testid="home-all-faq-link">
              Alle Fragen ansehen <ArrowRight className="h-4 w-4" />
            </Link>
          </section>

          <section aria-label="Einsatzgebiet">
            <SectionHeading eyebrow="Einsatzgebiet" title="Kfz-Gutachter für Köln und Umgebung" />
            <ServiceArea />
          </section>

          <CtaBand prefix="home-cta" />
        </div>
      </PageShell>
    </>
  );
}
